import { useEffect, useState } from "react";
import { api } from "../lib/api";

// Sumber Info (atribusi marketing, brief bos 11 Agu 2026) — field WAJIB di
// SEMUA transaksi penjualan, supaya laporan ROAS/CPL (Marketing Executive)
// bisa tahu asal transaksi. Backend: routers/sales.py
// `_validasi_dan_lengkapi_source_info`, dipakai ulang oleh TIGA jalur
// (create_sale/update_sale, nota_module.py, routers/imports.py) — SATU
// definisi "atribusi sah".
//
// KENAPA komponen ini dibagi (bukan disalin) ke InputNota.jsx & Sales.jsx:
// CLAUDE.md mencatat duplikasi helper pernah melahirkan penyimpangan (dua
// salinan next_code() berpisah jalan, pemilih template tersalin di 6
// berkas). Kedua form kasir WAJIB memakai nilai & label yang identik.
//
// Label manusiawi ("Instagram (organik)") TETAP ditulis di frontend —
// backend tidak tahu istilah yang dipahami kasir — tapi DAFTAR NILAI &
// status saklar darurat datang dari GET /sales/config/source-info, supaya
// backend menambah pilihan baru otomatis muncul di form tanpa deploy ulang
// frontend (lihat brief: "Ambil daftar pilihan dari sana, jangan hardcode").
export const SOURCE_INFO_LABELS = {
  ig_organic: "Instagram (organik)",
  tiktok_organic: "TikTok (organik)",
  kol: "KOL / Influencer",
  pfp: "Paid Promote (akun PFP)",
  ads: "Iklan berbayar (Ads)",
  walk_in: "Walk-in (datang langsung)",
  repeat: "Pelanggan lama (repeat)",
  referral: "Referral (dari pelanggan lain)",
};

// Nilai yang WAJIB menaut ke satu entitas aktif — backend SELALU menolaknya
// tanpa source_ref_id, apa pun status saklar darurat (lihat
// SOURCE_INFO_BUTUH_REF di routers/sales.py). UI mencegahnya lebih dulu di
// sini supaya kasir tidak menabrak 400 dari server.
const BUTUH_REF = new Set(["kol", "pfp"]);

/**
 * Hook: ambil status saklar darurat + pilihan Sumber Info + daftar KOL/PFP
 * aktif SEKALI saat form dibuka (bukan tiap ketikan/tiap baris barang — ini
 * jalur tersibuk di aplikasi, lihat brief). Dipanggil dari level modal
 * create/edit di InputNota.jsx/Sales.jsx.
 *
 * Endpoint KOL/PFP di sini SENGAJA endpoint ringkas milik `routers/sales.py`
 * (`/sales/source-info/{kol,pfp}-aktif`), BUKAN endpoint asli modul Marketing
 * (`/marketing/kol`, `/marketing/pfp/accounts`) — endpoint asli mensyaratkan
 * izin `marketing_kol.view`/`marketing_pfp.view` yang TIDAK dipegang kasir
 * biasa, dan akan 403 persis untuk role yang justru dituju field ini.
 */
export function useSourceInfoConfig() {
  const [state, setState] = useState({
    loaded: false, hardStop: true, choices: [],
    kolList: [], pfpList: [],
  });
  useEffect(() => {
    let alive = true;
    api.get("/sales/config/source-info").then(r => {
      if (!alive) return;
      setState(s => ({
        ...s, loaded: true,
        hardStop: r.data?.hard_stop !== false,
        choices: r.data?.choices || [],
      }));
    }).catch(() => { if (alive) setState(s => ({ ...s, loaded: true })); });
    api.get("/sales/source-info/kol-aktif").then(r => {
      if (alive) setState(s => ({ ...s, kolList: Array.isArray(r.data) ? r.data : [] }));
    }).catch(() => {});
    api.get("/sales/source-info/pfp-aktif").then(r => {
      if (alive) setState(s => ({ ...s, pfpList: Array.isArray(r.data) ? r.data : [] }));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return state;
}

/**
 * Validasi sisi klien SEBELUM submit — cermin persis aturan backend supaya
 * kasir tidak menabrak 400 dari server tanpa tahu jalan keluarnya:
 * - saklar darurat AKTIF & Sumber Info kosong -> wajib diisi
 * - kol/pfp TANPA rujukan terpilih -> SELALU ditolak, tidak peduli saklar
 * Mengembalikan pesan error (string) kalau tidak valid, atau null kalau valid.
 */
export function validasiSourceInfo(sourceInfo, sourceRefId, cfg) {
  const v = (sourceInfo || "").trim();
  if (!v) {
    return cfg.hardStop
      ? "Sumber Info (asal transaksi pelanggan) wajib dipilih sebelum disimpan."
      : null;
  }
  if (BUTUH_REF.has(v) && !(sourceRefId || "").trim()) {
    return `Sumber Info "${SOURCE_INFO_LABELS[v] || v}" wajib memilih nama dari daftar aktif di bawahnya.`;
  }
  return null;
}

/**
 * Field Sumber Info + (kalau relevan) pemilih KOL/akun PFP. Dipakai identik
 * di InputNota.jsx ("Buat Nota Baru") dan Sales.jsx ("Input Penjualan").
 *
 * Props:
 * - sourceInfo/sourceRefId: nilai terkini (mentah, sama seperti dikirim ke API)
 * - onChange(sourceInfo, sourceRefId): dipanggil untuk KEDUA perubahan (ganti
 *   pilihan Sumber Info otomatis mengosongkan rujukan lama)
 * - cfg: hasil useSourceInfoConfig()
 * - testidPrefix: awalan data-testid (dua form beda konteks -> beda prefix)
 */
export default function SourceInfoField({ sourceInfo, sourceRefId, onChange, cfg, testidPrefix = "source-info" }) {
  const choices = cfg.choices?.length ? cfg.choices : Object.keys(SOURCE_INFO_LABELS);
  const butuhRef = BUTUH_REF.has(sourceInfo);
  const daftarRef = sourceInfo === "kol" ? cfg.kolList : sourceInfo === "pfp" ? cfg.pfpList : [];
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-600">
        Sumber Info {cfg.hardStop && <span className="text-rose-600">*</span>}
      </label>
      <select
        value={sourceInfo || ""}
        onChange={e => onChange(e.target.value, "")}
        data-testid={`${testidPrefix}-select`}
        className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white"
      >
        <option value="">{cfg.hardStop ? "— pilih sumber transaksi —" : "— tidak diisi —"}</option>
        {choices.map(c => <option key={c} value={c}>{SOURCE_INFO_LABELS[c] || c}</option>)}
      </select>
      {butuhRef && (
        <>
          <select
            value={sourceRefId || ""}
            onChange={e => onChange(sourceInfo, e.target.value)}
            data-testid={`${testidPrefix}-ref-select`}
            className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white"
          >
            <option value="">{sourceInfo === "kol" ? "— pilih KOL —" : "— pilih akun PFP —"}</option>
            {daftarRef.map(r => (
              <option key={r.id} value={r.id}>{r.nama || r.nama_akun || r.id}</option>
            ))}
          </select>
          {daftarRef.length === 0 && (
            <p className="text-[10px] text-amber-600">
              Belum ada {sourceInfo === "kol" ? "KOL" : "akun PFP"} aktif terdaftar — tambahkan dulu di modul Marketing.
            </p>
          )}
        </>
      )}
    </div>
  );
}
